/**
 * Nutrición: registrar lo que se come y sumarlo.
 *
 * ### El límite, que no se mueve
 *
 * §10 de la spec prohíbe "prescripción automática de dietas o números de calorías". Este módulo
 * **registra y suma**; no recomienda. En particular:
 *
 * - No hay ninguna función que calcule un objetivo de calorías o de proteína. Si el usuario quiere
 *   un objetivo, lo escribe él, igual que el de series semanales. Sin objetivo, no hay objetivo.
 * - No hay reparto de macros sugerido, ni "te faltan carbohidratos", ni nada que se parezca a un
 *   consejo alimentario.
 * - La única lectura que la app hace sola es descriptiva: cuánta proteína por kilo de peso
 *   corporal registraste, que es el número con el que se compara cualquier cosa en la literatura.
 *
 * ### Los días sin registro se EXCLUYEN, no valen cero
 *
 * Es el mismo principio que las series omitidas (§4.3) y por la misma razón: un día que no
 * anotaste no es un día que comiste cero. Promediarlo como cero inventaría un déficit que no
 * existió, y este es justo el dato que se cruza con la masa magra. Todo promedio de acá abajo
 * cuenta sólo los días con al menos un registro, y devuelve cuántos días fueron.
 */

import { addDias, aTimestamp, diffDias, enVentana, ventana } from '../dates.ts';
import type { Fecha, MedidaCorporal } from '../types.ts';
import { enUnidad } from '../units.ts';

export type MomentoDelDia = 'desayuno' | 'almuerzo' | 'cena' | 'snack';

export const MOMENTOS: MomentoDelDia[] = ['desayuno', 'almuerzo', 'cena', 'snack'];

export const NOMBRE_MOMENTO: Record<MomentoDelDia, string> = {
  desayuno: 'Desayuno',
  almuerzo: 'Almuerzo',
  cena: 'Cena',
  snack: 'Snack',
};

export type FuenteComida = 'manual' | 'ia_texto' | 'ia_foto';

/** Una fila por alimento registrado, no por comida: así se puede corregir uno sin tocar el resto. */
export interface Alimento {
  id: string;
  user_id: string;
  fecha: Fecha;
  hora: string;
  momento: MomentoDelDia;
  /** Lo que el usuario escribió o lo que el modelo identificó: "150 g de pollo a la plancha". */
  descripcion: string;
  kcal: number;
  proteina_g: number;
  carbohidratos_g: number | null;
  grasa_g: number | null;
  fibra_g: number | null;
  /** Confianza de la estimación del modelo, 0–1. null si se cargó a mano. */
  confianza: number | null;
  fuente: FuenteComida;
  idem_key: string;
}

export interface TotalesDia {
  fecha: Fecha;
  kcal: number;
  proteina_g: number;
  carbohidratos_g: number;
  grasa_g: number;
  fibra_g: number;
  items: number;
  /** Confianza más baja del día: si hay una estimación floja, el total no es mejor que ella. */
  confianzaMinima: number | null;
  porMomento: Record<MomentoDelDia, { kcal: number; proteina_g: number; items: number }>;
}

const momentosVacios = (): TotalesDia['porMomento'] => ({
  desayuno: { kcal: 0, proteina_g: 0, items: 0 },
  almuerzo: { kcal: 0, proteina_g: 0, items: 0 },
  cena: { kcal: 0, proteina_g: 0, items: 0 },
  snack: { kcal: 0, proteina_g: 0, items: 0 },
});

export function totalesDelDia(alimentos: Alimento[], fecha: Fecha): TotalesDia {
  const delDia = alimentos.filter((a) => a.fecha === fecha);
  const porMomento = momentosVacios();

  let kcal = 0;
  let proteina = 0;
  let carbos = 0;
  let grasa = 0;
  let fibra = 0;
  let confianzaMinima: number | null = null;

  for (const a of delDia) {
    kcal += a.kcal;
    proteina += a.proteina_g;
    carbos += a.carbohidratos_g ?? 0;
    grasa += a.grasa_g ?? 0;
    fibra += a.fibra_g ?? 0;

    const m = porMomento[a.momento];
    if (m) {
      m.kcal += a.kcal;
      m.proteina_g += a.proteina_g;
      m.items += 1;
    }

    if (a.confianza !== null) {
      confianzaMinima = confianzaMinima === null ? a.confianza : Math.min(confianzaMinima, a.confianza);
    }
  }

  return {
    fecha,
    kcal,
    proteina_g: proteina,
    carbohidratos_g: carbos,
    grasa_g: grasa,
    fibra_g: fibra,
    items: delDia.length,
    confianzaMinima,
    porMomento,
  };
}

/** Un total por cada día CON registro, del más viejo al más nuevo. Los días vacíos no aparecen. */
export function totalesPorDia(alimentos: Alimento[]): TotalesDia[] {
  const fechas = [...new Set(alimentos.map((a) => a.fecha))].sort(
    (a, b) => aTimestamp(a) - aTimestamp(b),
  );
  return fechas.map((f) => totalesDelDia(alimentos, f));
}

// ---------------------------------------------------------------------------
// Promedios
// ---------------------------------------------------------------------------

export interface PromedioNutricion {
  /** Días con al menos un registro dentro de la ventana. */
  diasConRegistro: number;
  /** Días que abarca la ventana. La diferencia con el anterior es lo que NO se anotó. */
  diasDeVentana: number;
  kcal: number | null;
  proteina_g: number | null;
  carbohidratos_g: number | null;
  grasa_g: number | null;
  /** true si se anotó menos de la mitad de los días: el promedio no representa gran cosa. */
  registroEscaso: boolean;
}

/**
 * Promedio sobre los días CON registro. `diasDeVentana` y `diasConRegistro` van por separado a
 * propósito: la UI necesita poder decir "promedio de 4 días anotados de los últimos 7" en vez de
 * dar un número que parece un promedio semanal y no lo es.
 */
export function promedio(alimentos: Alimento[], hasta: Fecha, dias = 7): PromedioNutricion {
  const v = ventana(hasta, dias);
  const enRango = alimentos.filter((a) => enVentana(a.fecha, v));
  const porDia = totalesPorDia(enRango);

  if (porDia.length === 0) {
    return {
      diasConRegistro: 0,
      diasDeVentana: dias,
      kcal: null,
      proteina_g: null,
      carbohidratos_g: null,
      grasa_g: null,
      registroEscaso: true,
    };
  }

  const suma = <K extends keyof TotalesDia>(k: K): number =>
    porDia.reduce((a, d) => a + (d[k] as number), 0);

  return {
    diasConRegistro: porDia.length,
    diasDeVentana: dias,
    kcal: suma('kcal') / porDia.length,
    proteina_g: suma('proteina_g') / porDia.length,
    carbohidratos_g: suma('carbohidratos_g') / porDia.length,
    grasa_g: suma('grasa_g') / porDia.length,
    registroEscaso: porDia.length < dias / 2,
  };
}

// ---------------------------------------------------------------------------
// Proteína por kilo de peso corporal
// ---------------------------------------------------------------------------

export interface ProteinaPorKg {
  gramosPorKg: number | null;
  proteina_g: number | null;
  pesoKg: number | null;
  /** Fecha de la medición de peso usada. */
  pesoDe: Fecha | null;
  diasConRegistro: number;
}

/**
 * Gramos de proteína por kilo de peso corporal, que es la forma en que este número se compara con
 * cualquier referencia. Usa la última medición de peso disponible; sin peso registrado no devuelve
 * nada, en vez de asumir uno.
 *
 * No hay un valor "recomendado" acá. La app muestra el número; la interpretación no le toca.
 */
export function proteinaPorKg(
  alimentos: Alimento[],
  medidas: MedidaCorporal[],
  hasta: Fecha,
  dias = 7,
): ProteinaPorKg {
  const prom = promedio(alimentos, hasta, dias);

  const conPeso = medidas
    .filter((m) => m.fecha <= hasta && m.peso > 0)
    .sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha));
  const ultima = conPeso[conPeso.length - 1];

  if (!ultima || prom.proteina_g === null) {
    return {
      gramosPorKg: null,
      proteina_g: prom.proteina_g,
      pesoKg: ultima ? enUnidad({ valor: ultima.peso, unidad: ultima.unidad_peso }, 'kg') : null,
      pesoDe: ultima?.fecha ?? null,
      diasConRegistro: prom.diasConRegistro,
    };
  }

  const pesoKg = enUnidad({ valor: ultima.peso, unidad: ultima.unidad_peso }, 'kg');
  return {
    gramosPorKg: prom.proteina_g / pesoKg,
    proteina_g: prom.proteina_g,
    pesoKg,
    pesoDe: ultima.fecha,
    diasConRegistro: prom.diasConRegistro,
  };
}

// ---------------------------------------------------------------------------
// Objetivos — siempre los pone el usuario
// ---------------------------------------------------------------------------

export interface ObjetivosNutricion {
  /** Gramos de proteína por día. null = sin objetivo, y la app no inventa uno. */
  proteina_g: number | null;
  kcal: number | null;
}

export interface CumplimientoDia {
  fecha: Fecha;
  proteina_g: number;
  kcal: number;
  /** proteina / objetivo. null si no hay objetivo puesto. */
  cumplimientoProteina: number | null;
  cumplimientoKcal: number | null;
}

export function cumplimiento(
  alimentos: Alimento[],
  objetivos: ObjetivosNutricion,
  hasta: Fecha,
  dias = 14,
): CumplimientoDia[] {
  const v = ventana(hasta, dias);
  return totalesPorDia(alimentos.filter((a) => enVentana(a.fecha, v))).map((d) => ({
    fecha: d.fecha,
    proteina_g: d.proteina_g,
    kcal: d.kcal,
    cumplimientoProteina:
      objetivos.proteina_g && objetivos.proteina_g > 0 ? d.proteina_g / objetivos.proteina_g : null,
    cumplimientoKcal: objetivos.kcal && objetivos.kcal > 0 ? d.kcal / objetivos.kcal : null,
  }));
}

/**
 * Días seguidos por debajo del objetivo de proteína, mirando desde el último día CON registro
 * hacia atrás. Sirve para la única alerta de nutrición que la app emite, y sólo existe si el
 * usuario puso un objetivo.
 */
export function diasBajoObjetivoProteina(
  alimentos: Alimento[],
  objetivo: number | null,
  hasta: Fecha,
  tolerancia = 0.85,
): number {
  if (!objetivo || objetivo <= 0) return 0;

  const porDia = totalesPorDia(alimentos.filter((a) => a.fecha <= hasta)).reverse();
  let n = 0;
  for (const d of porDia) {
    if (d.proteina_g >= objetivo * tolerancia) break;
    n++;
  }
  return n;
}

// ---------------------------------------------------------------------------
// Constancia del registro
// ---------------------------------------------------------------------------

export interface ConstanciaRegistro {
  diasConRegistro: number;
  diasDeVentana: number;
  proporcion: number;
  /** Días corridos sin anotar nada, contando desde `hasta` hacia atrás. */
  diasSeguidosSinAnotar: number;
}

/**
 * Cuánto se está anotando. Es información sobre el registro, no un reproche: §10 prohíbe la
 * gamificación que castigue, así que acá no hay rachas que se "pierdan" ni nada que culpe.
 */
export function constancia(alimentos: Alimento[], hasta: Fecha, dias = 14): ConstanciaRegistro {
  const v = ventana(hasta, dias);
  const fechas = new Set(alimentos.filter((a) => enVentana(a.fecha, v)).map((a) => a.fecha));

  let sinAnotar = 0;
  for (let i = 0; i < dias; i++) {
    if (fechas.has(addDias(hasta, -i))) break;
    sinAnotar++;
  }

  return {
    diasConRegistro: fechas.size,
    diasDeVentana: dias,
    proporcion: fechas.size / dias,
    diasSeguidosSinAnotar: sinAnotar,
  };
}

/** Días desde el último registro de comida. null si nunca se registró nada. */
export function diasDesdeUltimoRegistro(alimentos: Alimento[], hasta: Fecha): number | null {
  const fechas = alimentos.filter((a) => a.fecha <= hasta).map((a) => a.fecha);
  if (fechas.length === 0) return null;
  const ultima = fechas.sort((a, b) => aTimestamp(a) - aTimestamp(b))[fechas.length - 1]!;
  return diffDias(ultima, hasta);
}
